import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { nanoid } from '@reduxjs/toolkit'

import { useChainId, useAccount } from 'wagmi'

import { Hash, Token } from 'types/entities'

import { useRequest } from 'hooks/api'
import { useAirdropper } from 'hooks/airdrop'
import { useIsWrongChainId } from 'hooks/wallet'
import { useCheckAllowanceERC20 } from 'hooks/token'

import { useClearPKWallet, useIsMaxApprove, useIsPendingApproveTxs } from 'state/user/hooks'
import {
  useToken,
  useAirdropList,
  useTotalTokens,
  useSetAirdropId,
  useSetAirdropParams,
  useSetExceptionsList,
} from 'state/airdrop/hooks'

import { isEther } from 'utils/crypto'

import { MAD_ASSEMBLY } from 'constants/addresses'
import { etherNumbers } from 'constants/variables'
import { errors as Errors } from 'constants/errors'
import { getTokenSubLink, TokenSubLinks } from 'constants/link'

import { useOpenModal } from 'modal'
import { newApproveTxModal } from 'modals/ApprovedTx/ApproveModal'

import { Button, Notification } from 'components/ui'

import { SectionLoader } from './Loader'
import { RecipientsTable } from './RecipientsTable'

import { Box, ApproveText } from './Style'

type RequestInput = { token: Token; address: Hash }

type Props = {
  isBalanceError: boolean
}

export function Recipients({ isBalanceError }: Props) {
  const chainId = useChainId()
  const { address } = useAccount()

  const token = useToken()
  const airdropper = useAirdropper()
  const totalTokens = useTotalTokens()
  const airdropList = useAirdropList()

  const isMaxApprove = useIsMaxApprove()

  const isWrongChainId = useIsWrongChainId()
  const isPendingApproveTxs = useIsPendingApproveTxs()

  const setAirdropId = useSetAirdropId()
  const clearPKWallet = useClearPKWallet()
  const setAirdropParams = useSetAirdropParams()
  const checkAllowance = useCheckAllowanceERC20()
  const setExceptionsList = useSetExceptionsList()

  const navigate = useNavigate()
  const openModal = useOpenModal()

  const approveAmount = useMemo(() => (isMaxApprove ? etherNumbers.MAX_UINT : totalTokens), [isMaxApprove, totalTokens])

  const createEmulationRequest = useRequest(async (params: RequestInput) => {
    try {
      if (!token?.address) {
        throw new Error(Errors.validation.TOKEN_IS_REQUIRED)
      }

      const { exceptions, batchSize, isDeflationary } = await airdropper.validateAirdrop({
        airdropList,
        token: params.token,
        customer: params.address,
      })

      setAirdropParams({ batchSize, isDeflationary, exceptions })

      return exceptions
    } catch (err) {
      setExceptionsList(airdropList.map((el) => ({ ...el, amount: el.amount.toString() })))
      throw err
    }
  })

  const checkAllowanceRequest = useRequest(async (params: RequestInput) => {
    if (isEther(params.token.address)) {
      return false
    }
    const currentAllowance = await checkAllowance(params.address, params.token.address, MAD_ASSEMBLY[chainId])

    return Boolean(currentAllowance < totalTokens)
  })

  const onContinue = async () => {
    setAirdropId(nanoid())

    if (!token || !token?.address) {
      throw new Error(Errors.validation.TOKEN_IS_REQUIRED)
    }

    if (!address) {
      throw new Error(Errors.validation.WALLET_REQUIRED)
    }

    if (isWrongChainId) {
      throw new Error(Errors.chainId.WRONG_CHAIN_ID)
    }

    const isApproveRequired = await checkAllowanceRequest.call({ token, address })

    if (isApproveRequired) {
      openModal(newApproveTxModal({ token, approveAmount }))
    } else {
      const exceptions = await createEmulationRequest.call({ token, address })

      const hasExceptions = exceptions && exceptions.length > 0

      if (Boolean(exceptions) && !hasExceptions) {
        navigate(getTokenSubLink(TokenSubLinks.AIRDROP))
      }
    }

    clearPKWallet()
  }

  const isLoading = useMemo(() => {
    return checkAllowanceRequest.isLoading || createEmulationRequest.isLoading
  }, [checkAllowanceRequest.isLoading, createEmulationRequest.isLoading])

  const isLoadingButton = useMemo(() => {
    return checkAllowanceRequest.isLoading || createEmulationRequest.isLoading || isPendingApproveTxs
  }, [checkAllowanceRequest.isLoading, createEmulationRequest.isLoading, isPendingApproveTxs])

  const isDisable = useMemo(() => {
    return isWrongChainId || isBalanceError
  }, [isWrongChainId, isBalanceError])

  const errorMessage = useMemo(() => {
    return (
      (checkAllowanceRequest.isError && checkAllowanceRequest.errorMessage) ||
      (createEmulationRequest.isError && createEmulationRequest.errorMessage)
    )
  }, [checkAllowanceRequest.errorMessage, createEmulationRequest.errorMessage])

  return (
    <>
      {isLoading && <SectionLoader />}

      <RecipientsTable />

      {errorMessage && <Notification type='error' margin='0 auto 1.6rem' message={errorMessage} />}

      <Box>
        <ApproveText>
          By&nbsp;clicking &rsquo;_continue&rsquo;, you confirm the validity of&nbsp;the distribution list
        </ApproveText>

        <Button fullSize size='large' onClick={onContinue} isLoading={isLoadingButton} disabled={isDisable}>
          _continue
        </Button>
      </Box>
    </>
  )
}
